// SPEC-021: Content Management System — TypeScript types mirroring the Go
// DTOs in internal/models/content.go. Keep this file in sync with the
// backend whenever columns or enum members change.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const ContentType = {
  Article: 'article',
  Audio: 'audio',
  Video: 'video',
  Mixed: 'mixed',
} as const;
export type ContentType = (typeof ContentType)[keyof typeof ContentType];

export const ContentStatus = {
  Draft: 'draft',
  PendingReview: 'pending_review',
  Published: 'published',
  Rejected: 'rejected',
  Archived: 'archived',
} as const;
export type ContentStatus = (typeof ContentStatus)[keyof typeof ContentStatus];

export const ContentVisibility = {
  Public: 'public',
  TeacherStudents: 'teacher_students',
  Tagged: 'tagged',
  Assigned: 'assigned',
} as const;
export type ContentVisibility = (typeof ContentVisibility)[keyof typeof ContentVisibility];

export const ContentSubscriptionTier = {
  Free: 'free',
  StudentBasic: 'student_basic',
  Growth: 'growth',
  Transformation: 'transformation',
  Mentorship: 'mentorship',
} as const;
export type ContentSubscriptionTier =
  (typeof ContentSubscriptionTier)[keyof typeof ContentSubscriptionTier];

export const ContentSeedLinkType = {
  Prerequisite: 'prerequisite',
  Companion: 'companion',
  Followup: 'followup',
} as const;
export type ContentSeedLinkType =
  (typeof ContentSeedLinkType)[keyof typeof ContentSeedLinkType];

// ---------------------------------------------------------------------------
// Tiptap document schema
// ---------------------------------------------------------------------------

export interface TiptapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TiptapBlock {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapBlock[];
  text?: string;
  marks?: TiptapMark[];
}

export interface TiptapDoc {
  type: 'doc';
  content: TiptapBlock[];
}

export const emptyTiptapDoc = (): TiptapDoc => ({ type: 'doc', content: [] });

// ---------------------------------------------------------------------------
// Visibility config — shape depends on the `visibility` field
// ---------------------------------------------------------------------------

export interface VisibilityConfig {
  tag_ids?: string[];
  match?: 'any' | 'all';
  user_ids?: string[];
}

// ---------------------------------------------------------------------------
// Core aggregates
// ---------------------------------------------------------------------------

export interface ContentMediaAsset {
  id: string;
  content_id: string;
  asset_type: 'image' | 'audio' | 'file';
  s3_object_key: string;
  mime_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
  created_at: string;
  updated_at: string;
  presigned_url?: string;
}

export interface ContentSeedLink {
  content_id: string;
  seed_id: string;
  link_type: ContentSeedLinkType;
  created_at: string;
}

export interface ContentTag {
  id: string;
  slug: string;
  name: string;
  color?: string;
  icon?: string;
}

export interface ContentItem {
  id: string;
  author_id: string;
  title: string;
  slug: string;
  summary?: string | null;
  cover_image_key?: string | null;
  content_type: ContentType;
  body: TiptapDoc;
  language: string;
  category?: string | null;
  estimated_minutes?: number | null;
  primary_audio_key?: string | null;
  primary_audio_duration_sec?: number | null;
  embed_provider?: 'youtube' | 'vimeo' | null;
  embed_url?: string | null;
  embed_video_id?: string | null;
  embed_duration_sec?: number | null;
  status: ContentStatus;
  visibility: ContentVisibility;
  visibility_config?: VisibilityConfig | null;
  min_subscription_tier: ContentSubscriptionTier;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  rejection_reason?: string | null;
  published_at?: string | null;
  scheduled_publish_at?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  // Hydrated by the service layer on GET:
  tags?: ContentTag[];
  media?: ContentMediaAsset[];
  seed_links?: ContentSeedLink[];
}

export interface ContentModerationLogEntry {
  id: number;
  content_id: string;
  actor_id: string;
  action: string;
  previous_status?: string | null;
  new_status?: string | null;
  reason?: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Request inputs (matches content.CreateInput in Go)
// ---------------------------------------------------------------------------

export interface ContentInput {
  title: string;
  summary?: string | null;
  cover_image_key?: string | null;
  content_type: ContentType;
  body: TiptapDoc;
  language?: string;
  category?: string | null;
  primary_audio_key?: string | null;
  embed_url?: string | null;
  visibility: ContentVisibility;
  visibility_config?: VisibilityConfig | null;
  min_subscription_tier: ContentSubscriptionTier;
  scheduled_publish_at?: string | null;
  tag_ids?: string[];
}

export interface UploadURLInput {
  asset_type: 'image' | 'audio' | 'file';
  mime_type: string;
  filename: string;
}

export interface UploadURLResponse {
  asset_id: string;
  s3_object_key: string;
  upload_url: string;
  expires_at: string;
}

export interface RegisterAssetInput {
  asset_id: string;
  asset_type: 'image' | 'audio' | 'file';
  s3_object_key: string;
  mime_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
}

export interface ContentListParams {
  status?: ContentStatus | '';
  limit?: number;
  offset?: number;
}

export interface ContentAdminListParams extends ContentListParams {
  author_id?: string;
  content_type?: ContentType | '';
  visibility?: ContentVisibility | '';
  search?: string;
}

export interface ContentFeedParams {
  type?: ContentType;
  tag_id?: string;
  bookmarked?: boolean;
  limit?: number;
  offset?: number;
}

export interface PaginatedContent {
  data: ContentItem[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export interface ContentKPIs {
  total_views: number;
  unique_viewers: number;
  completed_views: number;
  completion_rate: number;
  avg_time_seconds: number;
  like_count: number;
  bookmark_count: number;
}

export interface ContentDailyCount {
  day: string;
  count: number;
}

export interface ContentDropoffBucket {
  decile: number;
  count: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const contentStatusLabels: Record<ContentStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending review',
  published: 'Published',
  rejected: 'Rejected',
  archived: 'Archived',
};

export const contentTypeLabels: Record<ContentType, string> = {
  article: 'Article',
  audio: 'Audio',
  video: 'Video',
  mixed: 'Mixed',
};

export const contentVisibilityLabels: Record<ContentVisibility, string> = {
  public: 'Public',
  teacher_students: 'My students',
  tagged: 'Tagged students',
  assigned: 'Specific students',
};

export const contentTierLabels: Record<ContentSubscriptionTier, string> = {
  free: 'Free',
  student_basic: 'Student Basic',
  growth: 'Growth',
  transformation: 'Transformation',
  mentorship: 'Mentorship',
};
